import React from "react";
import { InlineMath } from "react-katex";

const ROMAN_VALUES = [
  [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
  [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
  [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
];

const TASK_LEVELS = [
  {
    direction: "fromRoman",
    difficulty: 1,
    label: "liczby do trzech cyfr bez zapisu odejmującego",
    numbers: [1, 2, 3, 5, 6, 7, 8, 11, 12, 13, 16, 18, 21, 23, 27, 38, 51, 57, 68, 101, 112, 123, 156, 237, 388, 501, 678, 888],
  },
  {
    direction: "fromRoman",
    difficulty: 2,
    label: "zapis odejmujący w liczbach najwyżej dwucyfrowych",
    numbers: [4, 9, 14, 19, 24, 29, 34, 39, 40, 44, 49, 54, 59, 64, 69, 74, 79, 84, 89, 90, 94, 99],
  },
  {
    direction: "fromRoman",
    difficulty: 3,
    label: "liczby trzycyfrowe z zapisem odejmującym",
    numbers: [104, 109, 145, 194, 249, 294, 394, 449, 494, 549, 594, 649, 694, 849, 894, 944],
  },
  {
    direction: "toRoman",
    difficulty: 1,
    label: "liczby do trzech cyfr bez zapisu odejmującego",
    numbers: [1, 2, 3, 5, 6, 7, 8, 11, 12, 13, 16, 18, 21, 23, 27, 38, 51, 57, 68, 101, 112, 123, 156, 237, 388, 501, 678, 888],
  },
  {
    direction: "toRoman",
    difficulty: 2,
    label: "zapis odejmujący w liczbach najwyżej dwucyfrowych",
    numbers: [4, 9, 14, 19, 24, 29, 34, 39, 40, 44, 49, 54, 59, 64, 69, 74, 79, 84, 89, 90, 94, 99],
  },
  {
    direction: "toRoman",
    difficulty: 3,
    label: "liczby trzycyfrowe z zapisem odejmującym",
    numbers: [104, 109, 145, 194, 249, 294, 394, 449, 494, 549, 594, 649, 694, 849, 894, 944],
  },
];

const SUBTRACTIVE_VALUES = {
  IV: "5-1=4",
  IX: "10-1=9",
  XL: "50-10=40",
  XC: "100-10=90",
  CD: "500-100=400",
  CM: "1000-100=900",
};

export function toRoman(value) {
  let remaining = value;
  let result = "";
  ROMAN_VALUES.forEach(([number, symbol]) => {
    while (remaining >= number) {
      result += symbol;
      remaining -= number;
    }
  });
  return result;
}

export function fromRoman(value) {
  const normalized = String(value || "").trim().toUpperCase();
  let total = 0;
  let index = 0;
  while (index < normalized.length) {
    let pair = null;
    for (const candidate of ROMAN_VALUES) {
      if (normalized.startsWith(candidate[1], index)) {
        pair = candidate;
        break;
      }
    }
    if (!pair) return Number.NaN;
    total += pair[0];
    index += pair[1].length;
  }
  return toRoman(total) === normalized ? total : Number.NaN;
}

function pickNumber(numbers) {
  return numbers[Math.floor(Math.random() * numbers.length)];
}

function splitIntoPlaceValues(number) {
  const places = [1000, 100, 10, 1];
  return places
    .map((place) => {
      const value = Math.floor(number / place) % 10 * place;
      return value ? { value, roman: toRoman(value) } : null;
    })
    .filter(Boolean);
}

function generateTask(level = TASK_LEVELS[0]) {
  const number = pickNumber(level.numbers);
  return {
    type: "RomanNumeralsTask",
    data: {
      direction: level.direction,
      difficulty: level.difficulty,
      levelLabel: level.label,
      number,
      roman: toRoman(number),
    },
    tags: [
      "systemy_liczbowe",
      "system_rzymski",
      level.direction,
      `poziom_${level.difficulty}`,
    ],
  };
}

function renderTask({ data }) {
  return data.direction === "toRoman" ? (
    <p>
      Zapisz liczbę <InlineMath math={`${data.number}`} /> w systemie rzymskim.
    </p>
  ) : (
    <p>
      Zapisz liczbę <InlineMath math={`\\mathrm{${data.roman}}`} /> w systemie dziesiętnym.
    </p>
  );
}

function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <input
      type="text"
      value={typeof userAnswer === "string" ? userAnswer : ""}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder="Wpisz odpowiedź"
      aria-label="Odpowiedź"
      autoComplete="off"
      style={{ maxWidth: 240, width: "100%", padding: "10px 12px", fontSize: "1rem" }}
    />
  );
}

function checkAnswer({ data }, userAnswer) {
  const answer = String(userAnswer || "").trim().toUpperCase();
  if (data.direction === "toRoman") return answer === data.roman;
  return Number(answer) === data.number;
}

function renderAnswer({ data }) {
  const math = data.direction === "toRoman"
    ? `\\mathrm{${data.roman}}`
    : `${data.number}`;
  return <InlineMath math={math} />;
}

function renderUserAnswer(userAnswer) {
  const answer = String(userAnswer || "").trim().toUpperCase();
  if (!answer) return <i>(brak odpowiedzi)</i>;
  const math = /^\d+$/.test(answer) ? answer : `\\mathrm{${answer}}`;
  return <InlineMath math={math} />;
}

function renderExplanation({ data }) {
  const parts = splitIntoPlaceValues(data.number);
  const decimalParts = parts.map(({ value }) => value).join("+");
  const romanParts = parts
    .map(({ value, roman }) => `\\underbrace{\\mathrm{${roman}}}_{${value}}`)
    .join("+");
  const conversions = parts
    .map(({ value, roman }) => `${value}=\\mathrm{${roman}}`)
    .join(",\\quad ");
  const subtractivePairs = Object.entries(SUBTRACTIVE_VALUES)
    .filter(([symbol]) => data.roman.includes(symbol))
    .map(([symbol, equation]) => `\\mathrm{${symbol}}=${equation}`)
    .join(",\\quad ");

  return (
    <div style={{ textAlign: "left" }}>
      {data.direction === "fromRoman" ? (
        <>
          <p>
            Czytamy zapis od lewej do prawej i dzielimy go na tysiące,
            setki, dziesiątki i jedności.
          </p>
          <p><InlineMath math={romanParts} /></p>
          <p>
            Dodajemy odczytane wartości:{" "}
            <InlineMath math={`${decimalParts}=${data.number}`} />.
          </p>
        </>
      ) : (
        <>
          <p>
            Rozkładamy liczbę w systemie dziesiętnym na setki, dziesiątki
            i jedności, a potem zamieniamy każdą część osobno.
          </p>
          <p><InlineMath math={`${data.number}=${decimalParts}`} /></p>
          <p><InlineMath math={conversions} /></p>
        </>
      )}
      {subtractivePairs ? (
        <p>
          Pojawia się tu zapis odejmujący:{" "}
          <InlineMath math={subtractivePairs} />.
        </p>
      ) : null}
      <p>
        <InlineMath math={`${data.number}=\\mathrm{${data.roman}}`} />
      </p>
    </div>
  );
}

const RomanNumeralsTask = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = { RomanNumeralsTask };

export function generateTasks() {
  return TASK_LEVELS.map((level) => generateTask(level));
}

export { renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };

const romanNumeralsSubtopic = {
  registry,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export default romanNumeralsSubtopic;
